/**
 * Reading service for client-side API calls
 */
import apiService from './apiService';
import fallbackService from './fallbackService';

// Flag to control whether to use fallback service
const USE_FALLBACK = false;

const readingService = {
  /**
   * Get all readings
   * @param {Object} params - Query parameters (startDate, endDate, limit)
   * @returns {Promise<Array>} - Array of readings
   */
  async getAllReadings(params = {}) {
    try {
      if (USE_FALLBACK) {
        console.log('Using fallback reading service');
        return fallbackService.readings.getAllReadings(params);
      }
      
      const queryParams = new URLSearchParams();
      
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.limit) queryParams.append('limit', params.limit);
      
      const queryString = queryParams.toString();
      const endpoint = `/readings${queryString ? `?${queryString}` : ''}`;
      
      return apiService.get(endpoint);
    } catch (error) {
      console.error('Get all readings error:', error);
      
      // If server is unavailable, fall back to mock data
      console.log('Falling back to mock readings');
      return fallbackService.readings.getAllReadings(params);
    }
  },

  /**
   * Get reading by ID
   * @param {number|string} id - Reading ID
   * @returns {Promise<Object>} - Reading data
   */
  async getReadingById(id) {
    try {
      if (USE_FALLBACK) {
        console.log(`Using fallback reading service for ID ${id}`);
        return fallbackService.readings.getReadingById(id);
      }
      
      return apiService.get(`/readings/${id}`);
    } catch (error) {
      console.error(`Get reading by ID ${id} error:`, error);
      
      // If server is unavailable, fall back to mock data
      console.log(`Falling back to mock reading for ID ${id}`);
      return fallbackService.readings.getReadingById(id);
    }
  },

  /**
   * Create a new reading
   * @param {Object} readingData - Reading data
   * @returns {Promise<Object>} - Created reading
   */
  async createReading(readingData) {
    try {
      if (USE_FALLBACK) {
        console.log('Using fallback reading service');
        return fallbackService.readings.createReading(readingData);
      }
      
      return apiService.post('/readings', readingData);
    } catch (error) {
      console.error('Create reading error:', error);
      
      // If server is unavailable, fall back to mock data
      console.log('Falling back to mock reading creation');
      return fallbackService.readings.createReading(readingData);
    }
  },

  async updateReading(id, readingData) {
    try {
      return apiService.put(`/readings/${id}`, readingData);
    } catch (error) {
      console.error(`Update reading ${id} error:`, error);
      throw error;
    }
  },

  async deleteReading(id) {
    try {
      return apiService.delete(`/readings/${id}`);
    } catch (error) {
      console.error(`Delete reading ${id} error:`, error);
      throw error;
    }
  },

  async deleteAllReadingsForDevice(deviceId, options = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (options.mode) queryParams.append('mode', options.mode);
      if (options.beforeDate) queryParams.append('beforeDate', options.beforeDate);
      if (options.startDate) queryParams.append('startDate', options.startDate);
      if (options.endDate) queryParams.append('endDate', options.endDate);

      const queryString = queryParams.toString();
      const endpoint = `/readings/device/${deviceId}/all${queryString ? `?${queryString}` : ''}`;
      return apiService.delete(endpoint);
    } catch (error) {
      console.error(`Delete all readings for device ${deviceId} error:`, error);
      throw error;
    }
  },

  /**
   * Get daily usage data
   * @param {Object} params - Query parameters (startDate, endDate, deviceId, siteId)
   * @returns {Promise<Array>} - Daily usage data
   */
  async getDailyUsage(params = {}) {
    try {
      if (USE_FALLBACK) {
        console.log('Using fallback daily usage service');
        return fallbackService.readings.getDailyUsage(params);
      }
      
      const queryParams = new URLSearchParams();
      
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.deviceId) queryParams.append('deviceId', params.deviceId);
      if (params.siteId) queryParams.append('siteId', params.siteId);
      
      const queryString = queryParams.toString();
      const endpoint = `/readings/daily-usage${queryString ? `?${queryString}` : ''}`;
      
      return apiService.get(endpoint);
    } catch (error) {
      console.error('Get daily usage error:', error);
      
      // If server is unavailable, fall back to mock data
      console.log('Falling back to mock daily usage');
      return fallbackService.readings.getDailyUsage(params);
    }
  },

  /**
   * Get monthly usage data
   * @param {Object} params - Query parameters (year, deviceId, siteId)
   * @returns {Promise<Array>} - Monthly usage data
   */
  async getMonthlyUsage(params = {}) {
    try {
      if (USE_FALLBACK) {
        console.log('Using fallback monthly usage service');
        return fallbackService.dashboard.getMonthlyUsage(params.months || 12, params.siteId);
      }
      
      const queryParams = new URLSearchParams();
      
      if (params.year) queryParams.append('year', params.year);
      if (params.deviceId) queryParams.append('deviceId', params.deviceId);
      if (params.siteId) queryParams.append('siteId', params.siteId);
      
      const queryString = queryParams.toString();
      const endpoint = `/readings/monthly-usage${queryString ? `?${queryString}` : ''}`;
      
      return apiService.get(endpoint);
    } catch (error) {
      console.error('Get monthly usage error:', error);
      
      // If server is unavailable, fall back to mock data
      console.log('Falling back to mock monthly usage');
      return fallbackService.dashboard.getMonthlyUsage(params.months || 12, params.siteId);
    }
  }
};

export default readingService;
